import React, { useState, useEffect, useRef } from 'react';
import { Quote, Star, MessageSquarePlus, Loader2, Calendar, BadgeCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { feedbackAPI } from '../services/api';
import FeedbackModal from './FeedbackModal';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';

const FeedbackCard = ({ feedback, index, mobile }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const textRef = useRef(null);
    const cardRef = useRef(null);

    // 1. Parallax Mouse Effect
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseX = useSpring(x, { stiffness: 500, damping: 100 });
    const mouseY = useSpring(y, { stiffness: 500, damping: 100 });

    const rotateX = useTransform(mouseY, [-0.5, 0.5], ["7deg", "-7deg"]);
    const rotateY = useTransform(mouseX, [-0.5, 0.5], ["-7deg", "7deg"]);

    const handleMouseMove = (e) => {
        if (mobile || !cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();

        const width = rect.width;
        const height = rect.height;

        const mouseXVal = e.clientX - rect.left;
        const mouseYVal = e.clientY - rect.top;

        const xPct = mouseXVal / width - 0.5;
        const yPct = mouseYVal / height - 0.5;

        x.set(xPct);
        y.set(yPct);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
        setIsExpanded(false);
    };

    // Check truncation
    useEffect(() => {
        if (textRef.current && (textRef.current.scrollHeight > textRef.current.clientHeight || feedback.feedbackMessage.length > 150)) {
            setShowReadMore(true);
        }
    }, [feedback.feedbackMessage]);

    return (
        <motion.div
            className={`relative h-full ${!isExpanded ? 'perspective-1000' : 'z-[60]'} ${mobile ? 'w-full mb-4' : 'w-[350px] md:w-[450px] flex-shrink-0 mx-4'} ${isExpanded ? 'z-50' : 'z-10'}`}
            // Remove perspective when expanded to allow true fixed positioning relative to viewport
            style={{ perspective: isExpanded ? 'none' : 1000 }}
        >
            {/* 
                Expansion Overlay - Click outside to close
             */}
            {isExpanded && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
                    onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
                />
            )}

            <motion.div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                layout // For smooth expansion
                style={{
                    rotateX: isExpanded ? 0 : rotateX,
                    rotateY: isExpanded ? 0 : rotateY,
                }}
                animate={{
                    scale: isExpanded ? 1 : 1,
                    boxShadow: isExpanded
                        ? "0px 25px 50px -12px rgba(14, 165, 233, 0.5)" // Expanded Glow
                        : "0px 5px 15px rgba(0,0,0,0.1)" // Standard
                }}
                transition={{
                    layout: { duration: 0.4, type: "spring", bounce: 0.2 }
                }}
                className={`
                    relative bg-[#001524]/95 backdrop-blur-2xl rounded-[23px] p-6 md:p-8 
                    border border-white/5 flex flex-col transform-style-3d transition-colors duration-300
                    ${isExpanded
                        ? 'fixed inset-0 m-auto w-[90vw] md:w-[600px] h-fit max-h-[85vh] overflow-y-auto z-50 border-sky-500/50 shadow-2xl'
                        : 'h-full hover:border-white/20 hover:bg-[#001e33]'
                    }
                `}
            >
                {/* Border Beam (CSS) - Only show when NOT expanded */}
                {!isExpanded && (
                    <div className="absolute inset-0 rounded-[23px] bg-gradient-to-r from-transparent via-sky-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-border-beam pointer-events-none" />
                )}

                {/* Header */}
                <div className="flex items-center gap-4 mb-5 shrink-0">
                    <div className="rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 h-10 w-10 md:h-12 md:w-12 flex items-center justify-center text-white font-bold text-lg shadow-lg shrink-0">
                        {feedback.customerName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h4 className="font-bold text-white tracking-wide text-sm md:text-base">
                                {feedback.customerName}
                            </h4>
                            <BadgeCheck className="h-4 w-4 text-sky-400 fill-sky-400/20" />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className={`h-3 w-3 ${i < feedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}`}
                                    />
                                ))}
                            </div>
                            <span className="text-[10px] text-gray-500 font-medium ml-1">Verified User</span>
                        </div>
                    </div>
                    <Quote className="h-6 w-6 md:h-8 md:w-8 text-white/5 ml-auto translate-z-10" />
                </div>

                {/* Message */}
                <div className="relative mb-4 flex-1">
                    <p
                        ref={textRef}
                        className={`text-gray-300 font-medium leading-relaxed italic text-sm md:text-base ${!isExpanded ? 'line-clamp-3 md:line-clamp-4' : ''}`}
                    >
                        "{feedback.feedbackMessage}"
                    </p>
                </div>

                {/* Footer */}
                <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                        <Calendar className="h-3 w-3" />
                        {new Date(feedback.createdDate).toLocaleDateString()}
                    </div>

                    {showReadMore && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            className="text-xs font-bold text-sky-400 hover:text-sky-300 flex items-center gap-1 transition-colors z-20"
                        >
                            {isExpanded ? (
                                <>Less <ChevronUp className="h-3 w-3" /></>
                            ) : (
                                <>Read More <ChevronDown className="h-3 w-3" /></>
                            )}
                        </button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

const FeedbackSection = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchFeedbacks = async () => {
        try {
            setLoading(true);
            const response = await feedbackAPI.getApprovedFeedback({ pageSize: 1000 });
            setFeedbacks(response.data.content);
        } catch (error) {
            console.error('Failed to load feedback:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeedbacks();
    }, []);

    // Split feedbacks into rows and duplicate for endless loop
    // Row 1: 0, 3, 6...
    // Row 2: 1, 4, 7...
    // Row 3: 2, 5, 8...
    const getRowData = (modulus, remainder) => {
        if (feedbacks.length === 0) return [];
        const subset = feedbacks.filter((_, i) => i % modulus === remainder);
        // Duplicate until we have at least 10 items for a smooth long scrolling loop
        let looped = [...subset];
        while (looped.length < 10 && looped.length > 0) {
            looped = [...looped, ...subset];
        }
        // Double check duplication for CSS marquee to work seamlessly (needs 2 sets)
        return [...looped, ...looped];
    };

    return (
        <section id="feedback" className="py-24 md:py-32 relative z-10 overflow-hidden bg-[#001524]">
            {/* Background Decorations */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500/5 rounded-full blur-3xl -z-10 animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDelay: '2s' }}></div>

            <div className="container mx-auto px-4 md:px-6 mb-16">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-black mb-8 backdrop-blur-md shadow-lg">
                            <div className="flex items-center justify-center p-1 bg-yellow-500 rounded-md shadow-[0_0_12px_rgba(234,179,8,0.4)]">
                                <Star className="h-3.5 w-3.5 text-white fill-white" />
                            </div>
                            <span className="tracking-[0.2em] uppercase opacity-90">Client Stories</span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight">What Our Clients Say</h2>
                        <p className="text-xl text-gray-200 leading-relaxed font-medium">
                            Join hundreds of satisfied businesses who trust Jay Info Tech for their IT infrastructure.
                        </p>
                    </div>

                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="group relative px-8 py-4 bg-sky-500 hover:bg-sky-400 text-white font-black rounded-2xl transition-all duration-300 shadow-[0_0_20px_rgba(14,165,233,0.3)] hover:shadow-[0_0_30px_rgba(14,165,233,0.5)] active:scale-95 flex items-center gap-3 z-20"
                    >
                        <MessageSquarePlus className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                        Share Your Story
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="h-10 w-10 animate-spin text-sky-500" />
                </div>
            ) : feedbacks.length === 0 ? (
                <div className="text-center py-20 container mx-auto">
                    <div className="bg-white/5 border border-white/10 rounded-[2.5rem] backdrop-blur-md p-10 max-w-lg mx-auto">
                        <Quote className="h-16 w-16 text-white/10 mx-auto mb-6" />
                        <h3 className="text-2xl font-bold text-white mb-2">Be the first to share!</h3>
                        <p className="text-gray-400">Your feedback helps us grow and serve you better.</p>
                    </div>
                </div>
            ) : (
                <div className="relative mask-gradient-x">
                    {/* MOBILE VIEW: Static Vertical List */}
                    <div className="md:hidden flex flex-col gap-6">
                        {feedbacks.map((f, i) => (
                            <div key={`mob-${i}`} className="w-full">
                                <FeedbackCard feedback={f} index={i} mobile={true} />
                            </div>
                        ))}
                    </div>

                    {/* DESKTOP VIEW: Virtual Window Marquee */}
                    <div className="hidden md:flex flex-col gap-8 md:gap-12">
                        {/* Row 1: Right to Left */}
                        <div className="w-full flex overflow-hidden group">
                            <div className="flex animate-marquee hover:[animation-play-state:paused]">
                                {getRowData(3, 0).map((f, i) => (
                                    <FeedbackCard key={`r1-${i}`} feedback={f} index={i} />
                                ))}
                            </div>
                        </div>

                        {/* Row 2: Left to Right (Reverse) */}
                        <div className="w-full flex overflow-hidden group">
                            <div className="flex animate-marquee-reverse hover:[animation-play-state:paused]">
                                {getRowData(3, 1).map((f, i) => (
                                    <FeedbackCard key={`r2-${i}`} feedback={f} index={i} />
                                ))}
                            </div>
                        </div>

                        {/* Row 3: Right to Left */}
                        <div className="hidden md:flex w-full overflow-hidden group">
                            <div className="flex animate-marquee hover:[animation-play-state:paused]">
                                {getRowData(3, 2).map((f, i) => (
                                    <FeedbackCard key={`r3-${i}`} feedback={f} index={i} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <FeedbackModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={fetchFeedbacks}
            />
        </section >
    );
};

export default FeedbackSection;
